import { useState, useEffect, useCallback } from 'react';

export type LocationStatus = 'pending' | 'granted' | 'denied';

interface Coords {
  latitude: number;
  longitude: number;
}

export function useGeolocation(active: boolean) {
  const [status, setStatus] = useState<LocationStatus>('pending');
  const [coords, setCoords] = useState<Coords | null>(null);

  const request = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('denied');
      return;
    }
    setStatus('pending');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setStatus('granted');
      },
      () => setStatus('denied')
    );
  }, []);

  useEffect(() => {
    let permission: PermissionStatus | null = null;
    let handleChange: (() => void) | null = null;

    if (typeof navigator !== 'undefined' && (navigator as any).permissions) {
      (navigator as any).permissions
        .query({ name: 'geolocation' })
        .then((p: PermissionStatus) => {
          permission = p;
          handleChange = () => {
            if (p.state === 'granted') {
              request();
            } else if (p.state === 'denied') {
              setStatus('denied');
              setCoords(null);
            } else {
              setStatus('pending');
            };
          };
          p.addEventListener('change', handleChange);
        })
        .catch(() => {});
    }

    return () => {
      if (permission && handleChange) {
        permission.removeEventListener('change', handleChange);
      }
    };
  }, [request]);

  useEffect(() => {
    if (active) {
      request();
    }
  }, [active, request]);

  return { status, coords, request };
}
